import React, { useCallback, useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  useTheme,
} from '@mui/material';
import { PrimaryGoal } from 'src/types/inputs';
import { FlowCta } from '../../flow-cta';
import {
  EventType,
  FormName,
  PageActionName,
  PageActionType,
  TrkLocation,
} from 'src/utils/analytics/constants';
import {
  useCallbackEvent,
  useImpressionEvent,
  useSegmentPageAction,
} from 'src/utils/analytics/hooks';
import { Input } from 'src/lib/constants';
import { JumpPageSectionProps } from '../../jump-page-body';

export const DEFAULT_LABEL = 'I need a credit card for...';

export type GoalOption = PrimaryGoal | '';
export type SelectOption = {
  value: GoalOption;
  label: string;
  disabled?: boolean;
};
import { getInputs } from 'src/redux/selectors';
import { useAppSelector } from 'src/redux/hooks';

export const options: SelectOption[] = [
  {
    value: PrimaryGoal.Travel,
    label: 'I want travel rewards and perks',
  },
  {
    value: PrimaryGoal.CashBack,
    label: 'I want to earn cash back on purchases',
  },
  { value: PrimaryGoal.Debt, label: 'I want to pay down debt' },
  {
    value: PrimaryGoal.BigPurchase,
    label: 'I want to make a big purchase',
  },
  {
    value: PrimaryGoal.BuildCredit,
    label: 'I want to build my credit',
  },
];

export const optionsWithDefault: SelectOption[] = [
  { value: '', label: DEFAULT_LABEL, disabled: true },
  ...options,
];

const CtaComponent: React.FC<JumpPageSectionProps> = ({
  analytics: { navigationId },
}) => {
  const inputs = useAppSelector(getInputs);
  const [selectedOption, setSelectedOption] = useState<GoalOption>(
    (inputs?.[Input.PrimaryGoal] ?? '') as GoalOption
  );
  const trackPageAction = useSegmentPageAction();
  const theme = useTheme();

  useImpressionEvent({
    navigationId,
    eventType: EventType.ShoppingGenericUpdated,
    payload: {},
  });

  const trackEvent = useCallbackEvent({
    navigationId,
  });

  const handleChange = useCallback(
    (e: SelectChangeEvent<PrimaryGoal>) => {
      const value = e?.target?.value;
      trackPageAction({
        actionName: PageActionName.PrimaryGoal,
        actionType: PageActionType.Select,
        actionInput: value,
      });
      trackEvent(EventType.ShoppingGenericUpdated, {
        form_name: FormName.Flow,
        page_number: 1,
        total_pages: undefined,
        is_valid: true,
        validation_failure_message: '',
        additional_attributes: {
          answer_value: value,
          question_name: Input.PrimaryGoal,
        },
      });
      setSelectedOption(value as GoalOption);
    },
    [trackEvent, trackPageAction]
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      bgcolor="common.white"
      borderRadius={2}
      marginTop={2}
      marginBottom={3}
      boxShadow={1}
      sx={{
        [theme.breakpoints.up('sm')]: {
          width: '80%',
        },
      }}
    >
      <Box
        display="flex"
        justifyContent="center"
        bgcolor="neutral.lightest"
        padding={1}
        width="100%"
      >
        <Typography variant="textBold" textAlign="center">
          Why are you looking for a new credit card?
        </Typography>
      </Box>
      <Box
        display="flex"
        flexDirection="row"
        paddingX={3}
        paddingY={3.5}
        width="100%"
        sx={{
          [theme.breakpoints.down('md')]: {
            paddingX: 2,
            flexDirection: 'column',
          },
        }}
      >
        <FormControl
          sx={{
            m: 1,
            [theme.breakpoints.up('md')]: {
              minWidth: 300,
            },
            zIndex: 0,
          }}
          size="small"
        >
          <InputLabel
            htmlFor="primary-goal-select"
            sx={{
              color: !selectedOption ? 'neutral.light' : 'text.primary',
              borderColor: !selectedOption ? 'neutral.light' : 'text.primary',
            }}
          >
            {DEFAULT_LABEL}
          </InputLabel>
          <Select
            id="primary-goal-select"
            label={DEFAULT_LABEL}
            value={selectedOption}
            onChange={handleChange}
            sx={{
              marginRight: 1,
              borderRadius: 1,
              color: !selectedOption ? 'neutral.light' : 'text.primary',
              borderColor: !selectedOption ? 'neutral.light' : 'text.primary',
              [theme.breakpoints.down('md')]: {
                marginBottom: 1,
                marginRight: 0,
              },
            }}
          >
            {(selectedOption ? options : optionsWithDefault).map((opt) => (
              <MenuItem
                key={opt.label}
                value={opt.value}
                disabled={opt.disabled}
              >
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FlowCta
          primaryGoal={selectedOption}
          trkLocation={TrkLocation.JumpPageHero}
          sx={{
            borderRadius: 1,
            height: '2.5rem',
            marginTop: 1,
            paddingRight: 3,
            [theme.breakpoints.up('md')]: {
              maxWidth: '8rem',
              marginX: 0,
              padding: 0,
            },
          }}
        >
          <Typography variant="accentSmall">Get started</Typography>
        </FlowCta>
      </Box>
    </Box>
  );
};

export default CtaComponent;
